import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import DashboardLayout from './DashboardLayout';
import Select from 'react-select';
import ConfirmationModal from './ConfirmationModal';
import { useSuccessAlert, useErrorAlert, useWarningAlert } from './AlertSystem';
import { blogsApi } from '../lib/api';
import { extractErrorMessage } from '../lib/utils';
import { useAuth } from '../lib/AuthContext';
import './AdminBlogsManagement.css';

interface Blog {
    id: number;
    title: string;
    slug: string;
    excerpt?: string;
    content: string;
    featuredImage?: string;
    category?: string;
    tags: string[];
    status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
    views: number;
    publishedAt?: string;
    createdAt: string;
    updatedAt: string;
    author?: {
        id: number;
        name: string;
        email?: string;
    };
}

const AdminBlogsManagement: React.FC = () => {
    // Auth context
    const { hasPermission, isSuperAdmin } = useAuth();
    
    // Alert system hooks
    const showSuccess = useSuccessAlert();
    const showError = useErrorAlert();
    const showWarning = useWarningAlert();

    // Prevent multiple simultaneous fetches
    const isFetchingRef = useRef(false);

    const [allBlogs, setAllBlogs] = useState<Blog[]>([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [selectedBlogs, setSelectedBlogs] = useState<number[]>([]);
    const [selectedBlog, setSelectedBlog] = useState<Blog | null>(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [blogToDelete, setBlogToDelete] = useState<Blog | null>(null);
    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        featuredImage: '',
        category: '',
        tags: [] as string[],
        status: 'DRAFT' as Blog['status']
    });
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [error, setError] = useState<string | null>(null);
    const [tagInput, setTagInput] = useState('');
    const blogsPerPage = 10;

    // React-select options
    const statusOptions = [
        { value: 'all', label: 'All Statuses' },
        { value: 'DRAFT', label: 'Draft' },
        { value: 'PUBLISHED', label: 'Published' },
        { value: 'ARCHIVED', label: 'Archived' }
    ];

    const categoryOptions = [
        { value: 'all', label: 'All Categories' },
        { value: 'Cybersecurity', label: 'Cybersecurity' },
        { value: 'Technology', label: 'Technology' },
        { value: 'Business', label: 'Business' },
        { value: 'Industry News', label: 'Industry News' },
        { value: 'Tutorials', label: 'Tutorials' },
        { value: 'Best Practices', label: 'Best Practices' },
        { value: 'Case Studies', label: 'Case Studies' }
    ];

    // Custom styles for react-select
    const selectStyles = {
        control: (provided: any, state: any) => ({
            ...provided,
            background: 'rgba(26, 26, 39, 0.8)',
            border: `1px solid ${state.isFocused ? 'var(--primary-purple)' : 'rgba(148, 163, 184, 0.2)'}`,
            borderRadius: '8px',
            padding: '2px 4px',
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif",
            minHeight: '40px',
            boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.15)' : 'none',
            cursor: 'pointer',
            '&:hover': {
                border: `1px solid ${state.isFocused ? 'var(--primary-purple)' : 'rgba(99, 102, 241, 0.4)'}`
            }
        }),
        menu: (provided: any) => ({
            ...provided,
            background: 'rgba(26, 26, 39, 0.95)',
            border: '1px solid rgba(99, 102, 241, 0.3)',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            zIndex: 99999999,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)'
        }),
        option: (provided: any, state: any) => ({
            ...provided,
            background: state.isSelected 
                ? 'rgba(99, 102, 241, 0.2)' 
                : state.isFocused 
                ? 'rgba(255, 255, 255, 0.08)' 
                : 'transparent',
            color: state.isSelected ? 'var(--primary-purple)' : 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif",
            padding: '10px 14px',
            cursor: 'pointer',
            '&:hover': {
                background: state.isSelected ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255, 255, 255, 0.08)'
            }
        }),
        singleValue: (provided: any) => ({
            ...provided,
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif"
        }),
        placeholder: (provided: any) => ({
            ...provided,
            color: 'var(--text-muted)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif"
        }),
        input: (provided: any) => ({
            ...provided,
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif"
        }),
        dropdownIndicator: (provided: any) => ({
            ...provided,
            color: 'var(--text-muted)',
            padding: '6px',
            '&:hover': {
                color: 'var(--text-primary)'
            }
        }),
        indicatorSeparator: () => ({
            display: 'none'
        })
    };

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Client-side filtering for better responsiveness
    const filteredBlogs = useMemo(() => {
        let filtered = allBlogs;

        // Apply search filter
        if (debouncedSearchQuery.trim()) {
            const query = debouncedSearchQuery.toLowerCase();
            filtered = filtered.filter(blog =>
                blog.title.toLowerCase().includes(query) ||
                blog.excerpt?.toLowerCase().includes(query) ||
                blog.content.toLowerCase().includes(query) ||
                blog.category?.toLowerCase().includes(query) ||
                blog.tags.some(tag => tag.toLowerCase().includes(query))
            );
        }

        // Apply status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(blog => blog.status === statusFilter);
        }

        // Apply category filter
        if (categoryFilter !== 'all') {
            filtered = filtered.filter(blog => blog.category === categoryFilter);
        }

        return filtered;
    }, [allBlogs, debouncedSearchQuery, statusFilter, categoryFilter]);

    // Pagination
    const totalPages = Math.ceil(filteredBlogs.length / blogsPerPage);
    const startIndex = (currentPage - 1) * blogsPerPage;
    const paginatedBlogs = filteredBlogs.slice(startIndex, startIndex + blogsPerPage);

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchQuery, statusFilter, categoryFilter]);

    // Initial data fetch
    useEffect(() => {
        const fetchBlogs = async () => {
            // Prevent multiple simultaneous fetches
            if (isFetchingRef.current) return;
            
            try {
                isFetchingRef.current = true;
                setLoading(true);
                setError(null);

                const response = await blogsApi.getAdminBlogs({ limit: 1000 }); // Fetch all blogs for admin with client-side filtering

                if (response.success && response.data) {
                    // Handle both array and object with blogs property
                    const blogs = Array.isArray(response.data) ? response.data : (response.data.blogs || []);
                    setAllBlogs(blogs);
                } else {
                    throw new Error(extractErrorMessage(response));
                }
            } catch (error) {
                console.error('Error fetching admin blogs:', error);
                setError(error instanceof Error ? error.message : 'Failed to fetch admin blogs');
                showError(error instanceof Error ? error.message : 'Failed to fetch admin blogs', 'Error Loading Admin Blogs');
                setAllBlogs([]);
            } finally {
                setLoading(false);
                isFetchingRef.current = false;
            }
        };

        fetchBlogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only fetch once on mount

    // Optimistic update helper
    const updateBlogOptimistically = useCallback((blogId: number, updates: Partial<Blog>) => {
        setAllBlogs(prev => prev.map(blog => 
            blog.id === blogId ? { ...blog, ...updates } : blog
        ));
    }, []);

    // Add new blog optimistically
    const addBlogOptimistically = useCallback((newBlog: Blog) => {
        setAllBlogs(prev => [newBlog, ...prev]);
    }, []);

    // Remove blog optimistically
    const removeBlogOptimistically = useCallback((blogId: number) => {
        setAllBlogs(prev => prev.filter(blog => blog.id !== blogId));
    }, []);

    const formatDate = (dateString: string) => {
        if (!dateString) return 'Never';
        
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short'
        });
    };

    const getTimeAgo = (dateString: string) => {
        if (!dateString) return 'Never';
        
        const date = new Date(dateString);
        const now = new Date();
        
        const diffInMs = now.getTime() - date.getTime();
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

        if (diffInMinutes < 1) {
            return 'Just now';
        } else if (diffInMinutes < 60) {
            return `${diffInMinutes}m ago`;
        } else if (diffInHours < 24) {
            return `${diffInHours}h ago`;
        } else if (diffInDays < 7) {
            return `${diffInDays}d ago`;
        } else {
            return formatDate(dateString);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'PUBLISHED': return '#10b981';
            case 'DRAFT': return '#f59e0b';
            case 'ARCHIVED': return '#6b7280';
            default: return '#6b7280';
        }
    };

    const handleSelectBlog = (blogId: number) => {
        setSelectedBlogs(prev =>
            prev.includes(blogId)
                ? prev.filter(id => id !== blogId)
                : [...prev, blogId]
        );
    };

    const handleSelectAll = () => {
        if (selectedBlogs.length === paginatedBlogs.length) {
            setSelectedBlogs([]);
        } else {
            setSelectedBlogs(paginatedBlogs.map(blog => blog.id));
        }
    };

    const handleBulkStatusChange = async (newStatus: string) => {
        if (!newStatus || selectedBlogs.length === 0) return;

        setUpdating(true);
        const selectedCount = selectedBlogs.length;
        
        // Optimistic update
        const originalBlogs = [...allBlogs];
        selectedBlogs.forEach(blogId => {
            updateBlogOptimistically(blogId, { status: newStatus as Blog['status'] });
        });

        try {
            // Update each blog individually since there's no bulk API
            const updatePromises = selectedBlogs.map(blogId => {
                // Map status to published boolean for the existing API
                const published = newStatus === 'PUBLISHED';
                return blogsApi.updateBlog(blogId.toString(), { 
                    published,
                    status: newStatus
                });
            });

            const results = await Promise.allSettled(updatePromises);
            const failed = results.filter(result => result.status === 'rejected');

            if (failed.length > 0) {
                // Revert optimistic update on failure
                setAllBlogs(originalBlogs);
                throw new Error(`Failed to update ${failed.length} blog(s)`);
            }

            setSelectedBlogs([]);
            showSuccess(
                `Successfully updated ${selectedCount} blog${selectedCount > 1 ? 's' : ''} to ${newStatus.toLowerCase()}`,
                'Bulk Update Complete'
            );
        } catch (error) {
            console.error('Error updating blogs:', error);
            setError(error instanceof Error ? error.message : 'Failed to update blogs');
            showError(
                error instanceof Error ? error.message : 'Failed to update blogs',
                'Bulk Update Failed'
            );
            // Revert optimistic update on error
            setAllBlogs(originalBlogs);
        } finally {
            setUpdating(false);
        }
    };

    const handleDeleteBlog = async (blog: Blog) => {
        setBlogToDelete(blog);
        setIsDeleteModalOpen(true);
    };

    const confirmDeleteBlog = async () => {
        if (!blogToDelete) return;

        setUpdating(true);
        
        // Optimistic update
        removeBlogOptimistically(blogToDelete.id);

        try {
            const response = await blogsApi.deleteBlog(blogToDelete.id.toString());

            if (!response.success) {
                // Revert optimistic update on failure
                setAllBlogs(prev => [blogToDelete, ...prev]);
                throw new Error(extractErrorMessage(response));
            }
            
            showSuccess(`Blog "${blogToDelete.title}" has been deleted successfully`, 'Blog Deleted');
        } catch (error) {
            console.error('Error deleting blog:', error);
            setError(error instanceof Error ? error.message : 'Failed to delete blog');
            showError(
                error instanceof Error ? error.message : 'Failed to delete blog',
                'Error Deleting Blog'
            );
            // Revert optimistic update on error
            setAllBlogs(prev => [blogToDelete, ...prev]);
        } finally {
            setUpdating(false);
            setIsDeleteModalOpen(false);
            setBlogToDelete(null);
        }
    };

    const handleEditBlog = (blog: Blog) => {
        setSelectedBlog(blog);
        setFormData({
            title: blog.title,
            slug: blog.slug,
            excerpt: blog.excerpt || '',
            content: blog.content,
            featuredImage: blog.featuredImage || '',
            category: blog.category || '',
            tags: blog.tags || [],
            status: blog.status
        });
        setFormErrors({});
        setIsEditModalOpen(true);
    };

    const handleCreateBlog = () => {
        setSelectedBlog(null);
        setFormData({
            title: '',
            slug: '',
            excerpt: '',
            content: '',
            featuredImage: '',
            category: '',
            tags: [],
            status: 'DRAFT'
        });
        setFormErrors({});
        setIsCreateModalOpen(true);
    };

    const validateForm = () => {
        const errors: Record<string, string> = {};

        if (!formData.title.trim()) {
            errors.title = 'Title is required';
        } else if (formData.title.length > 255) {
            errors.title = 'Title must be less than 255 characters';
        }

        if (!formData.content.trim()) {
            errors.content = 'Content is required';
        }

        if (formData.excerpt && formData.excerpt.length > 500) {
            errors.excerpt = 'Excerpt must be less than 500 characters';
        }

        if (formData.featuredImage && !/^https?:\/\/.+/.test(formData.featuredImage)) {
            errors.featuredImage = 'Featured image must be a valid URL';
        }

        setFormErrors(errors);
        
        if (Object.keys(errors).length > 0) {
            showWarning('Please fix the form errors before submitting', 'Form Validation Failed');
            return false;
        }
        
        return true;
    };

    const generateSlug = (title: string): string => {
        return title
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
            .replace(/\s+/g, '-') // Replace spaces with hyphens
            .replace(/-+/g, '-') // Remove duplicate hyphens
            .trim()
            .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
    };

    const handleSubmitForm = async (isEdit: boolean) => {
        if (!validateForm()) return;

        setIsSubmitting(true);
        
        try {
            // Generate slug if not provided
            const slug = formData.slug || generateSlug(formData.title);

            const blogData = {
                title: formData.title,
                content: formData.content,
                excerpt: formData.excerpt,
                category: formData.category,
                featured_image: formData.featuredImage,
                published: formData.status === 'PUBLISHED',
                status: formData.status,
                tags: formData.tags
            };

            if (isEdit && selectedBlog) {
                // Optimistic update for edit
                updateBlogOptimistically(selectedBlog.id, { ...formData, slug });
                
                const response = await blogsApi.updateBlog(selectedBlog.id.toString(), blogData);
                
                if (!response.success) {
                    // Revert optimistic update on failure
                    updateBlogOptimistically(selectedBlog.id, selectedBlog);
                    throw new Error(extractErrorMessage(response));
                }
                
                showSuccess(`Blog "${formData.title}" has been updated successfully`, 'Blog Updated');
            } else {
                // Create new blog
                const response = await blogsApi.createBlog(blogData);
                
                if (response.success && response.data && response.data.blog) {
                    const newBlog: Blog = {
                        id: response.data.blog.id,
                        title: response.data.blog.title,
                        slug: response.data.blog.slug,
                        excerpt: response.data.blog.excerpt,
                        content: response.data.blog.content,
                        featuredImage: response.data.blog.featuredImage,
                        category: response.data.blog.category,
                        tags: response.data.blog.tags || [],
                        status: response.data.blog.status,
                        views: response.data.blog.views || 0,
                        publishedAt: response.data.blog.publishedAt,
                        createdAt: response.data.blog.createdAt,
                        updatedAt: response.data.blog.updatedAt,
                        author: response.data.blog.author
                    };
                    
                    addBlogOptimistically(newBlog);
                    setCurrentPage(1); // Go to first page to see new blog
                    showSuccess(`Blog "${formData.title}" has been created successfully`, 'Blog Created');
                } else {
                    throw new Error(extractErrorMessage(response));
                }
            }

            // Close modals
            setIsCreateModalOpen(false);
            setIsEditModalOpen(false);
            setSelectedBlog(null);
            
            // Reset form
            setFormData({
                title: '',
                slug: '',
                excerpt: '',
                content: '',
                featuredImage: '',
                category: '',
                tags: [],
                status: 'DRAFT'
            });
            setFormErrors({});
        } catch (error) {
            console.error(`Error ${isEdit ? 'updating' : 'creating'} blog:`, error);
            setError(error instanceof Error ? error.message : `Failed to ${isEdit ? 'update' : 'create'} blog`);
            showError(
                error instanceof Error ? error.message : `Failed to ${isEdit ? 'update' : 'create'} blog`,
                `Error ${isEdit ? 'Updating' : 'Creating'} Blog`
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAddTag = () => {
        if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, tagInput.trim()]
            }));
            setTagInput('');
        }
    };

    const handleRemoveTag = (tagToRemove: string) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    if (loading) {
        return (
            <DashboardLayout title="Blogs Management">
                <div className="admin-blogs-loading">
                    <div className="admin-blogs-loading-spinner">
                        <i className="ri-loader-4-line"></i>
                    </div>
                    <div className="admin-blogs-loading-text">Loading blogs...</div>
                </div>
            </DashboardLayout>
        );
    }

    if (error) {
        return (
            <DashboardLayout title="Blogs Management">
                <div className="admin-blogs-error">
                    <div className="admin-blogs-error-icon">
                        <i className="ri-error-warning-line"></i>
                    </div>
                    <div className="admin-blogs-error-content">
                        <h3>Failed to Load Blogs</h3>
                        <p>{error}</p>
                        <button
                            className="admin-blogs-retry-btn"
                            onClick={() => window.location.reload()}
                        >
                            <i className="ri-refresh-line"></i>
                            Try Again
                        </button>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout title="Blogs Management">
            <div className="admin-blogs">
                {/* Header */}
                <div className="admin-blogs-header">
                    <div className="admin-blogs-header-content">
                        <h1 className="admin-blogs-title">Blogs Management</h1>
                        <p className="admin-blogs-subtitle">
                            Create, manage, and publish blog posts to share insights, tutorials, and industry news
                        </p>
                    </div>
                    <div className="admin-blogs-stats">
                        <div className="admin-blogs-stat">
                            <div className="admin-blogs-stat-value">{allBlogs.length}</div>
                            <div className="admin-blogs-stat-label">Total</div>
                        </div>
                        <div className="admin-blogs-stat">
                            <div className="admin-blogs-stat-value">
                                {allBlogs.filter((b: Blog) => b.status === 'PUBLISHED').length}
                            </div>
                            <div className="admin-blogs-stat-label">Published</div>
                        </div>
                        <div className="admin-blogs-stat">
                            <div className="admin-blogs-stat-value">
                                {allBlogs.filter((b: Blog) => b.status === 'DRAFT').length}
                            </div>
                            <div className="admin-blogs-stat-label">Drafts</div>
                        </div>
                    </div>
                </div>

                {/* Filters & Actions */}
                <div className={`admin-blogs-filters ${updating ? 'updating' : ''}`}>
                    <div className="admin-blogs-filters-row">
                        <div className="admin-blogs-search-container">
                            <div className="admin-blogs-search">
                                <i className="ri-search-line"></i>
                                <input
                                    type="text"
                                    placeholder="Search blogs by title, content, category, or tags..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className={`admin-blogs-search-input ${debouncedSearchQuery !== searchQuery ? 'searching' : ''}`}
                                />
                                {searchQuery && (
                                    <button
                                        className="admin-blogs-search-clear"
                                        onClick={() => setSearchQuery('')}
                                        title="Clear search"
                                    >
                                        <i className="ri-close-line"></i>
                                    </button>
                                )}
                                {debouncedSearchQuery !== searchQuery && (
                                    <div className="admin-blogs-search-loading">
                                        <i className="ri-loader-4-line rotating"></i>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <div className="admin-blogs-actions">
                            {(hasPermission('blogs_create') || isSuperAdmin()) && (
                                <button
                                    className="admin-blogs-create-btn"
                                    onClick={handleCreateBlog}
                                >
                                    <i className="ri-add-line"></i>
                                    <span>New Blog</span>
                                </button>
                            )}
                        </div>
                    </div>
                    
                    <div className="admin-blogs-filters-secondary">
                        <div className="admin-blogs-filter-group">
                            <div className="admin-blogs-filter-item">
                                <label className="admin-blogs-filter-label">Status</label>
                                <Select
                                    options={statusOptions}
                                    value={statusOptions.find(option => option.value === statusFilter)}
                                    onChange={(option) => setStatusFilter(option?.value || 'all')}
                                    placeholder="All"
                                    styles={selectStyles}
                                    isSearchable={false}
                                    className="admin-blogs-filter-select"
                                    isDisabled={updating}
                                />
                            </div>
                            
                            <div className="admin-blogs-filter-item">
                                <label className="admin-blogs-filter-label">Category</label>
                                <Select
                                    options={categoryOptions}
                                    value={categoryOptions.find(option => option.value === categoryFilter)}
                                    onChange={(option) => setCategoryFilter(option?.value || 'all')}
                                    placeholder="All"
                                    styles={selectStyles}
                                    isSearchable={false}
                                    className="admin-blogs-filter-select"
                                    isDisabled={updating}
                                />
                            </div>
                            
                            <div className="admin-blogs-filter-actions">
                                <button
                                    className="admin-blogs-filter-clear"
                                    onClick={() => {
                                        setSearchQuery('');
                                        setStatusFilter('all');
                                        setCategoryFilter('all');
                                    }}
                                    title="Clear all filters"
                                    disabled={updating}
                                >
                                    <i className="ri-filter-off-line"></i>
                                    Clear Filters
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bulk Actions */}
                {selectedBlogs.length > 0 && (
                    <div className="admin-blogs-bulk-actions">
                        <div className="admin-blogs-bulk-info">
                            <span>{selectedBlogs.length} blog(s) selected</span>
                        </div>
                        <div className="admin-blogs-bulk-buttons">
                            <Select
                                options={[
                                    { value: 'PUBLISHED', label: 'Publish' },
                                    { value: 'DRAFT', label: 'Move to Draft' },
                                    { value: 'ARCHIVED', label: 'Archive' }
                                ]}
                                value={null}
                                onChange={(option) => handleBulkStatusChange(option?.value || '')}
                                placeholder="Change Status"
                                styles={selectStyles}
                                isSearchable={false}
                            />
                            <button
                                className="admin-blogs-bulk-btn admin-blogs-bulk-clear"
                                onClick={() => {
                                    setSelectedBlogs([]);
                                }}
                            >
                                <i className="ri-close-line"></i>
                                Clear Selection
                            </button>
                        </div>
                    </div>
                )}

                {/* Blogs List */}
                <div className="admin-blogs-list-container">
                    {updating && (
                        <div className="admin-blogs-updating-indicator">
                            <i className="ri-loader-4-line rotating"></i>
                            <span>Updating blogs...</span>
                        </div>
                    )}
                    {paginatedBlogs.length === 0 ? (
                        <div className="admin-blogs-empty">
                            <i className="ri-article-line"></i>
                            <h3>No blogs found</h3>
                            <p>Try adjusting your search or filter criteria</p>
                        </div>
                    ) : (
                        <div className={`admin-blogs-list ${updating ? 'updating' : ''}`}>
                            <div className="admin-blogs-list-header">
                                <label className="admin-blogs-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={selectedBlogs.length === paginatedBlogs.length}
                                        onChange={handleSelectAll}
                                    />
                                    <div className="admin-blogs-checkbox-checkmark">
                                        <i className="ri-check-line"></i>
                                    </div>
                                </label>
                                <div className="admin-blogs-list-header-title">Blog</div>
                                <div className="admin-blogs-list-header-title">Status</div>
                                <div className="admin-blogs-list-header-title">Category</div>
                                <div className="admin-blogs-list-header-title">Views</div>
                                <div className="admin-blogs-list-header-title">Created</div>
                                <div className="admin-blogs-list-header-title">Actions</div>
                            </div>
                            {paginatedBlogs.map((blog) => (
                                <div key={blog.id} className="admin-blog-card">
                                    <label className="admin-blogs-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={selectedBlogs.includes(blog.id)}
                                            onChange={() => handleSelectBlog(blog.id)}
                                        />
                                        <div className="admin-blogs-checkbox-checkmark">
                                            <i className="ri-check-line"></i>
                                        </div>
                                    </label>
                                    <div className="admin-blog-main">
                                        <div className="admin-blog-icon">
                                            <i className="ri-article-line"></i>
                                        </div>
                                        <div className="admin-blog-content">
                                            <h4 className="admin-blog-title">{blog.title}</h4>
                                            <p className="admin-blog-excerpt">{blog.excerpt || 'No excerpt available'}</p>
                                            <div className="admin-blog-meta">
                                                <span className="admin-blog-slug">
                                                    <i className="ri-link"></i>
                                                    {blog.slug}
                                                </span>
                                                {blog.author && (
                                                    <span className="admin-blog-author">
                                                        <i className="ri-user-line"></i>
                                                        {blog.author.name}
                                                    </span>
                                                )}
                                                {blog.tags.length > 0 && (
                                                    <span className="admin-blog-tags">
                                                        <i className="ri-price-tag-3-line"></i>
                                                        {blog.tags.slice(0, 2).join(', ')}
                                                        {blog.tags.length > 2 && ` +${blog.tags.length - 2}`}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="admin-blog-status">
                                        <span
                                            className="admin-blog-status-badge"
                                            style={{
                                                backgroundColor: `${getStatusColor(blog.status)}20`,
                                                color: getStatusColor(blog.status)
                                            }}
                                        >
                                            {blog.status.toLowerCase()}
                                        </span>
                                    </div>
                                    <div className="admin-blog-category">
                                        <span className="admin-blog-category-badge">
                                            {blog.category || 'Uncategorized'}
                                        </span>
                                    </div>
                                    <div className="admin-blog-views">
                                        <span className="admin-blog-views-count">
                                            <i className="ri-eye-line"></i>
                                            {blog.views}
                                        </span>
                                    </div>
                                    <div className="admin-blog-time">
                                        <span className="admin-blog-time-main">{getTimeAgo(blog.createdAt)}</span>
                                        <span className="admin-blog-time-full">{formatDate(blog.createdAt)}</span>
                                    </div>
                                    <div className="admin-blog-actions">
                                        {(hasPermission('blogs_edit') || isSuperAdmin()) && (
                                            <button
                                                className="admin-blog-action-btn"
                                                onClick={() => handleEditBlog(blog)}
                                                title="Edit blog"
                                            >
                                                <i className="ri-edit-line"></i>
                                            </button>
                                        )}
                                        {(hasPermission('blogs_delete') || isSuperAdmin()) && (
                                            <button
                                                className="admin-blog-action-btn admin-blog-delete-btn"
                                                onClick={() => handleDeleteBlog(blog)}
                                                title="Delete blog"
                                            >
                                                <i className="ri-delete-bin-line"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className={`admin-blogs-pagination ${updating ? 'updating' : ''}`}>
                        <button
                            className="admin-blogs-pagination-btn"
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1 || updating}
                        >
                            <i className="ri-arrow-left-line"></i>
                            Previous
                        </button>
                        <div className="admin-blogs-pagination-info">
                            Page {currentPage} of {totalPages} ({filteredBlogs.length} blogs)
                        </div>
                        <button
                            className="admin-blogs-pagination-btn"
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages || updating}
                        >
                            Next
                            <i className="ri-arrow-right-line"></i>
                        </button>
                    </div>
                )}

                {/* Create Modal */}
                {isCreateModalOpen && (
                    <div className="admin-blogs-modal-overlay" onClick={() => setIsCreateModalOpen(false)}>
                        <div className="admin-blogs-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="admin-blogs-modal-header">
                                <div className="admin-blogs-modal-header-content">
                                    <h3>Create New Blog</h3>
                                    <p>Write and publish a new blog post to share with your audience</p>
                                </div>
                                <button onClick={() => setIsCreateModalOpen(false)}>
                                    <i className="ri-close-line"></i>
                                </button>
                            </div>
                            <div className="admin-blogs-modal-content">
                                <form onSubmit={(e) => { e.preventDefault(); handleSubmitForm(false); }}>
                                    <div className="admin-blogs-form-section">
                                        <h4>Basic Information</h4>
                                        
                                        <div className="admin-blogs-form-group">
                                            <label>Title *</label>
                                            <input
                                                type="text"
                                                value={formData.title}
                                                onChange={(e) => {
                                                    setFormData({ ...formData, title: e.target.value });
                                                    // Auto-generate slug if slug is empty
                                                    if (!formData.slug) {
                                                        setFormData(prev => ({ ...prev, slug: generateSlug(e.target.value) }));
                                                    }
                                                }}
                                                placeholder="Enter a compelling blog title"
                                                className={formErrors.title ? 'error' : ''}
                                                disabled={isSubmitting}
                                            />
                                            {formErrors.title && <span className="error-text">{formErrors.title}</span>}
                                        </div>

                                        <div className="admin-blogs-form-group">
                                            <label>Slug</label>
                                            <input
                                                type="text"
                                                value={formData.slug}
                                                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                                placeholder="blog-url-slug (auto-generated from title)"
                                                disabled={isSubmitting}
                                            />
                                            <small>Used in the URL. Leave empty to auto-generate from title.</small>
                                        </div>

                                        <div className="admin-blogs-form-group">
                                            <label>Excerpt</label>
                                            <textarea
                                                value={formData.excerpt}
                                                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                                                placeholder="Brief summary of the blog post"
                                                rows={2}
                                                className={formErrors.excerpt ? 'error' : ''}
                                                disabled={isSubmitting}
                                            />
                                            {formErrors.excerpt && <span className="error-text">{formErrors.excerpt}</span>}
                                        </div>

                                        <div className="admin-blogs-form-group">
                                            <label>Content *</label>
                                            <textarea
                                                value={formData.content}
                                                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                                placeholder="Write your blog content here..."
                                                rows={8}
                                                className={formErrors.content ? 'error' : ''}
                                                disabled={isSubmitting}
                                            />
                                            {formErrors.content && <span className="error-text">{formErrors.content}</span>}
                                        </div>
                                    </div>

                                    <div className="admin-blogs-form-section">
                                        <h4>Metadata</h4>
                                        
                                        <div className="admin-blogs-form-grid">
                                            <div className="admin-blogs-form-group">
                                                <label>Category</label>
                                                <Select
                                                    value={categoryOptions.slice(1).find(option => option.value === formData.category) || null}
                                                    onChange={(selectedOption: any) => setFormData({ ...formData, category: selectedOption?.value || '' })}
                                                    options={categoryOptions.slice(1)}
                                                    styles={selectStyles}
                                                    isDisabled={isSubmitting}
                                                    placeholder="Select category"
                                                    isClearable
                                                />
                                            </div>

                                            <div className="admin-blogs-form-group">
                                                <label>Status</label>
                                                <Select
                                                    value={statusOptions.slice(1).find(option => option.value === formData.status)}
                                                    onChange={(selectedOption: any) => setFormData({ ...formData, status: selectedOption.value })}
                                                    options={statusOptions.slice(1)}
                                                    styles={selectStyles}
                                                    isDisabled={isSubmitting}
                                                    placeholder="Select status"
                                                />
                                            </div>
                                        </div>

                                        <div className="admin-blogs-form-group">
                                            <label>Featured Image URL</label>
                                            <input
                                                type="url"
                                                value={formData.featuredImage}
                                                onChange={(e) => setFormData({ ...formData, featuredImage: e.target.value })}
                                                placeholder="https://example.com/image.jpg"
                                                className={formErrors.featuredImage ? 'error' : ''}
                                                disabled={isSubmitting}
                                            />
                                            {formErrors.featuredImage && <span className="error-text">{formErrors.featuredImage}</span>}
                                        </div>

                                        <div className="admin-blogs-form-group">
                                            <label>Tags</label>
                                            <div className="admin-blogs-tags-input">
                                                <input
                                                    type="text"
                                                    value={tagInput}
                                                    onChange={(e) => setTagInput(e.target.value)}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleAddTag();
                                                        }
                                                    }}
                                                    placeholder="Add tag and press Enter"
                                                    disabled={isSubmitting}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={handleAddTag}
                                                    disabled={isSubmitting || !tagInput.trim()}
                                                    className="admin-blogs-add-tag-btn"
                                                >
                                                    <i className="ri-add-line"></i>
                                                </button>
                                            </div>
                                            {formData.tags.length > 0 && (
                                                <div className="admin-blogs-tags-list">
                                                    {formData.tags.map((tag, index) => (
                                                        <span key={index} className="admin-blogs-tag">
                                                            {tag}
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveTag(tag)}
                                                                disabled={isSubmitting}
                                                            >
                                                                <i className="ri-close-line"></i>
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="admin-blogs-modal-actions">
                                        <button
                                            type="button"
                                            onClick={() => setIsCreateModalOpen(false)}
                                            disabled={isSubmitting}
                                            className="admin-blogs-btn-secondary"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="admin-blogs-btn-primary"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <i className="ri-loader-4-line rotating"></i>
                                                    Creating Blog...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="ri-add-line"></i>
                                                    Create Blog
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* Edit Modal */}
                {isEditModalOpen && selectedBlog && (
                    <div className="admin-blogs-modal-overlay" onClick={() => setIsEditModalOpen(false)}>
                        <div className="admin-blogs-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="admin-blogs-modal-header">
                                <div className="admin-blogs-modal-header-content">
                                    <h3>Edit Blog</h3>
                                    <p>Update blog information and content</p>
                                </div>
                                <button onClick={() => setIsEditModalOpen(false)}>
                                    <i className="ri-close-line"></i>
                                </button>
                            </div>
                            <div className="admin-blogs-modal-content">
                                <form onSubmit={(e) => { e.preventDefault(); handleSubmitForm(true); }}>
                                    <div className="admin-blogs-form-section">
                                        <h4>Basic Information</h4>
                                        
                                        <div className="admin-blogs-form-group">
                                            <label>Title *</label>
                                            <input
                                                type="text"
                                                value={formData.title}
                                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                                placeholder="Enter a compelling blog title"
                                                className={formErrors.title ? 'error' : ''}
                                                disabled={isSubmitting}
                                            />
                                            {formErrors.title && <span className="error-text">{formErrors.title}</span>}
                                        </div>

                                        <div className="admin-blogs-form-group">
                                            <label>Slug</label>
                                            <input
                                                type="text"
                                                value={formData.slug}
                                                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                                placeholder="blog-url-slug"
                                                disabled={isSubmitting}
                                            />
                                        </div>

                                        <div className="admin-blogs-form-group">
                                            <label>Excerpt</label>
                                            <textarea
                                                value={formData.excerpt}
                                                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                                                placeholder="Brief summary of the blog post"
                                                rows={2}
                                                className={formErrors.excerpt ? 'error' : ''}
                                                disabled={isSubmitting}
                                            />
                                            {formErrors.excerpt && <span className="error-text">{formErrors.excerpt}</span>}
                                        </div>

                                        <div className="admin-blogs-form-group">
                                            <label>Content *</label>
                                            <textarea
                                                value={formData.content}
                                                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                                placeholder="Write your blog content here..."
                                                rows={8}
                                                className={formErrors.content ? 'error' : ''}
                                                disabled={isSubmitting}
                                            />
                                            {formErrors.content && <span className="error-text">{formErrors.content}</span>}
                                        </div>
                                    </div>

                                    <div className="admin-blogs-form-section">
                                        <h4>Metadata</h4>
                                        
                                        <div className="admin-blogs-form-grid">
                                            <div className="admin-blogs-form-group">
                                                <label>Category</label>
                                                <Select
                                                    value={categoryOptions.slice(1).find(option => option.value === formData.category) || null}
                                                    onChange={(selectedOption: any) => setFormData({ ...formData, category: selectedOption?.value || '' })}
                                                    options={categoryOptions.slice(1)}
                                                    styles={selectStyles}
                                                    isDisabled={isSubmitting}
                                                    placeholder="Select category"
                                                    isClearable
                                                />
                                            </div>

                                            <div className="admin-blogs-form-group">
                                                <label>Status</label>
                                                <Select
                                                    value={statusOptions.slice(1).find(option => option.value === formData.status)}
                                                    onChange={(selectedOption: any) => setFormData({ ...formData, status: selectedOption.value })}
                                                    options={statusOptions.slice(1)}
                                                    styles={selectStyles}
                                                    isDisabled={isSubmitting}
                                                    placeholder="Select status"
                                                />
                                            </div>
                                        </div>

                                        <div className="admin-blogs-form-group">
                                            <label>Featured Image URL</label>
                                            <input
                                                type="url"
                                                value={formData.featuredImage}
                                                onChange={(e) => setFormData({ ...formData, featuredImage: e.target.value })}
                                                placeholder="https://example.com/image.jpg"
                                                className={formErrors.featuredImage ? 'error' : ''}
                                                disabled={isSubmitting}
                                            />
                                            {formErrors.featuredImage && <span className="error-text">{formErrors.featuredImage}</span>}
                                        </div>

                                        <div className="admin-blogs-form-group">
                                            <label>Tags</label>
                                            <div className="admin-blogs-tags-input">
                                                <input
                                                    type="text"
                                                    value={tagInput}
                                                    onChange={(e) => setTagInput(e.target.value)}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleAddTag();
                                                        }
                                                    }}
                                                    placeholder="Add tag and press Enter"
                                                    disabled={isSubmitting}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={handleAddTag}
                                                    disabled={isSubmitting || !tagInput.trim()}
                                                    className="admin-blogs-add-tag-btn"
                                                >
                                                    <i className="ri-add-line"></i>
                                                </button>
                                            </div>
                                            {formData.tags.length > 0 && (
                                                <div className="admin-blogs-tags-list">
                                                    {formData.tags.map((tag, index) => (
                                                        <span key={index} className="admin-blogs-tag">
                                                            {tag}
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveTag(tag)}
                                                                disabled={isSubmitting}
                                                            >
                                                                <i className="ri-close-line"></i>
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="admin-blogs-modal-actions">
                                        <button
                                            type="button"
                                            onClick={() => setIsEditModalOpen(false)}
                                            disabled={isSubmitting}
                                            className="admin-blogs-btn-secondary"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="admin-blogs-btn-primary"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <i className="ri-loader-4-line rotating"></i>
                                                    Updating Blog...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="ri-save-line"></i>
                                                    Update Blog
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                <ConfirmationModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onConfirm={confirmDeleteBlog}
                    title="Delete Blog"
                    message="Are you sure you want to delete this blog? This action cannot be undone."
                    confirmText="Delete Blog"
                    type="danger"
                    isLoading={updating}
                >
                    {blogToDelete && (
                        <div className="admin-blogs-delete-modal-blog">
                            <div className="admin-blogs-delete-modal-blog-icon">
                                <i className="ri-article-line"></i>
                            </div>
                            <div className="admin-blogs-delete-modal-blog-info">
                                <h4>{blogToDelete.title}</h4>
                                <p>{blogToDelete.excerpt || 'No excerpt available'}</p>
                                <div className="admin-blogs-delete-modal-blog-meta">
                                    <span className="admin-blogs-delete-modal-blog-status" 
                                          style={{ color: getStatusColor(blogToDelete.status) }}>
                                        {blogToDelete.status.toLowerCase()}
                                    </span>
                                    <span className="admin-blogs-delete-modal-blog-category">
                                        {blogToDelete.category || 'Uncategorized'}
                                    </span>
                                    <span className="admin-blogs-delete-modal-blog-views">
                                        {blogToDelete.views} views
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </ConfirmationModal>
            </div>
        </DashboardLayout>
    );
};

export default AdminBlogsManagement;